/**
 * useScenarioSearch — Backend-driven search for the financial model editor.
 *
 * Fires a debounced query to GET /modelscenario/{id}/search and merges the
 * returned matches + ancestor chain into the scenario-tree cache. Once merged,
 * the existing isMatchSearchString recursion finds the matches (which may have
 * been unreachable because their branch wasn't lazy-loaded).
 *
 * Query key: ['search-data', scenarioId, kind, query]
 *
 * Note: the merge is additive only — items previously loaded (by section load,
 * node-expand, or earlier searches) stay in the tree. The "partially-loaded
 * children" issue is handled in useNodeExpand by checking all children, not
 * just the first, before deciding whether to fetch.
 */

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { searchScenarioModelData, getSensitivity } from "../../api/scenarios";
import {
  mapModelDataList,
  mapField,
  copyFields,
} from "../../../features/financial-editor/services/dataMappers";
import { SCENARIO_TREE_KEY, type EditorState, createInitialState } from "./useScenarioTree";

export const SEARCH_DATA_KEY = "search-data";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

interface UseScenarioSearchParams {
  scenarioId: number;
  currentSensitivity: any;
  query: string;
  kind: "Input" | "Output";
}

export function useScenarioSearch({
  scenarioId,
  currentSensitivity,
  query,
  kind,
}: UseScenarioSearchParams) {
  const queryClient = useQueryClient();
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 250);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!scenarioId || q.length < 2) {
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);

    (async () => {
      try {
        const responseData = await queryClient.fetchQuery({
          queryKey: [SEARCH_DATA_KEY, scenarioId, kind, q],
          queryFn: () => searchScenarioModelData(scenarioId, q, kind),
          staleTime: 30_000,
        });
        if (cancelled) return;

        const searchItems = mapModelDataList(
          ((responseData as any)[kind.toLowerCase()] || []) as any[],
        );

        let sensitivityData: any = null;
        if (currentSensitivity && currentSensitivity !== "null") {
          try {
            sensitivityData = await getSensitivity(currentSensitivity.id);
          } catch (err) {
            console.error("Failed to fetch sensitivity during search:", err);
          }
        }
        if (cancelled) return;

        queryClient.setQueryData<EditorState>(
          [SCENARIO_TREE_KEY, scenarioId],
          (old) => {
            const prev = old ?? createInitialState();

            if (kind === "Input") {
              const newItems = { ...prev.Input.tree.items, ...searchItems };
              if (sensitivityData) {
                sensitivityData.model_datas?.forEach((sObj: any) => {
                  const it = newItems[sObj.original_model_data_id];
                  if (it) {
                    newItems[sObj.original_model_data_id] = {
                      ...it,
                      sensitivityModelData: sObj,
                    };
                  }
                });
              }
              return {
                ...prev,
                Input: {
                  tree: { items: newItems, rootId: prev.Input.tree.rootId },
                  field_values: {
                    ...prev.Input.field_values,
                    ...mapField(searchItems),
                  },
                },
              };
            }

            const newItems = { ...prev.Output.tree.items, ...searchItems };
            if (sensitivityData) {
              sensitivityData.output?.forEach((obj: any) => {
                const cur = newItems[obj.id.toString()];
                if (cur) {
                  const patched = { ...cur };
                  copyFields(obj, patched);
                  newItems[obj.id.toString()] = patched;
                }
              });
            }
            return {
              ...prev,
              Output: {
                tree: { items: newItems, rootId: prev.Output.tree.rootId },
                field_values: {
                  ...prev.Output.field_values,
                  ...mapField(searchItems),
                },
              },
            };
          },
        );
      } catch (err) {
        console.error("Scenario search failed:", err);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, scenarioId, kind, currentSensitivity, queryClient]);

  return { searchLoading };
}
